import { useState } from 'react'
import type { AssumptionRow as Row, ForecastEffect, ProjectTableRow } from '../../api/cashReport'
import AssumptionRow from './AssumptionRow.tsx'

/** One active cash_on_hand row in the list (newest wins). */
export function singletonBankRows(rows: Row[]): Row[] {
  const banks = rows.filter((r) => r.kind === 'cash_on_hand')
  if (banks.length <= 1) return rows
  const keep = banks.reduce((best, r) => {
    const tb = Date.parse(String(r.updated_at || r.created_at || 0)) || 0
    const ta = Date.parse(String(best.updated_at || best.created_at || 0)) || 0
    return tb >= ta ? r : best
  })
  let placed = false
  return rows.filter((r) => {
    if (r.kind !== 'cash_on_hand') return true
    if (r.id !== keep.id || placed) return false
    placed = true
    return true
  })
}

type Props = {
  rows: Row[]
  projects: ProjectTableRow[]
  auditCount?: number
  startAdd?: { kind?: string } | null
  onConsumedAdd?: () => void
  onEditBank?: (row: Row) => void
  onSave: (input: {
    id?: string
    kind: string
    amount_cents: number
    effective_date: string
    project_id: number | null
    reason?: string | null
  }) => Promise<void>
  onDelete: (id: string) => Promise<void>
  onPreviewDelete: (id: string) => Promise<ForecastEffect>
}

export default function ManualInputs({
  rows, projects, auditCount = 0, startAdd, onConsumedAdd, onEditBank, onSave, onDelete, onPreviewDelete,
}: Props) {
  const [adding, setAdding] = useState(false)
  const openAdd = adding || !!startAdd
  const visible = singletonBankRows(rows)

  return (
    <section id="cash-report-cash-on-hand" className="cr-section" data-testid="cr-manual-inputs">
      <div className="cr-section-label">
        <span>Manual forecast inputs</span>
        <span>Hover a row to Edit or Delete · always visible on touch</span>
      </div>
      <div className="cr-panel">
        {visible.map((row) => (
          <AssumptionRow
            key={row.id}
            row={row}
            projects={projects}
            onEditBank={onEditBank}
            onSave={onSave}
            onDelete={onDelete}
            onPreviewDelete={onPreviewDelete}
          />
        ))}
        {openAdd ? (
          <AssumptionRow
            row={startAdd?.kind ? { id: '', kind: startAdd.kind, amount_cents: null, effective_date: '' } : null}
            adding
            projects={projects}
            onCancelAdd={() => { setAdding(false); onConsumedAdd?.() }}
            onSave={async (input) => {
              await onSave({ ...input, kind: input.kind || startAdd?.kind || 'one_time_expense' })
              setAdding(false)
              onConsumedAdd?.()
            }}
            onDelete={async () => undefined}
            onPreviewDelete={async () => ({ this_week: { before_cents: 0, after_cents: 0 }, lowest: { before_cents: 0, after_cents: 0 } })}
          />
        ) : null}
        <div className="cr-add-row">
          <button type="button" className="cr-btn" data-testid="cr-assumption-add" onClick={() => setAdding(true)}>+ Add</button>
        </div>
      </div>
      <p className="cr-quiet"><b>Editing rule:</b> manual assumptions can be edited here. Approved invoices, vendor bills, and derived totals open their project or source record instead of being edited inside the report.</p>
      <p hidden data-testid="cr-audit-count">{auditCount}</p>
    </section>
  )
}
